import {useEffect} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {
  addOption,
  removeOption,
  selectActiveOption,
  selectActiveOptionDisplayOrder,
  selectProposalOptions,
  setActiveOptionDisplayOrder,
  updateOption,
} from '../../../../../app-store/ProposalSlice'

const DEFAULT_LABELS = ['Good', 'Better', 'Best']

const MAX_PROPOSALS = 3
const MIN_PROPOSALS = 1

const createOption = position => ({
  // optionId: crypto.randomUUID(),
  optionName: DEFAULT_LABELS[position] ?? `Proposal ${position + 1}`,
  displayOrder: position + 1,
  items: [],
  coupons: [],
})

export function useProposalOptions() {
  const dispatch = useDispatch()

  const proposalOptions = useSelector(selectProposalOptions)
  const activeDisplayOrder = useSelector(selectActiveOptionDisplayOrder)
  const activeOption = useSelector(selectActiveOption)

  // -----------------------------------------------------
  // Initialize new proposal
  // -----------------------------------------------------

  useEffect(() => {
    if (proposalOptions.length) return

    dispatch(addOption(createOption(0)))
    dispatch(addOption(createOption(1)))
  }, [dispatch, proposalOptions.length])

  // -----------------------------------------------------
  // Select first option
  // -----------------------------------------------------

  useEffect(() => {
    if (!activeDisplayOrder && proposalOptions.length) {
      dispatch(setActiveOptionDisplayOrder(proposalOptions[0].displayOrder))
    }
  }, [proposalOptions, activeDisplayOrder, dispatch])

  // -----------------------------------------------------
  // Derived UI data
  // -----------------------------------------------------

  const proposalOptiontabs = proposalOptions.map(option => ({
    label: option.optionName,
    id: option.displayOrder,
  }))

  // -----------------------------------------------------
  // Actions
  // -----------------------------------------------------

  const handleAddOption = () => {
    if (proposalOptions.length >= MAX_PROPOSALS) return

    const option = createOption(proposalOptions.length)

    dispatch(addOption(option))
    dispatch(setActiveOptionDisplayOrder(option.displayOrder))
  }

  const handleDuplicateOption = () => {
    if (proposalOptions.length >= MAX_PROPOSALS) return

    const current = proposalOptions.find(
      option => option.displayOrder === activeDisplayOrder,
    )

    if (!current) return

    const duplicated = {
      ...structuredClone(current),
      displayOrder: proposalOptions.length + 1,
      optionName:
        DEFAULT_LABELS[proposalOptions.length] ??
        `Proposal ${proposalOptions.length + 1}`,
    }

    dispatch(addOption(duplicated))
    dispatch(setActiveOptionDisplayOrder(duplicated.displayOrder))
  }

  const handleDeleteOption = displayOrder => {
    if (proposalOptions.length <= MIN_PROPOSALS) return

    dispatch(removeOption(displayOrder))

    if (activeDisplayOrder === displayOrder) {
      const remaining = proposalOptions.filter(
        option => option.displayOrder !== displayOrder,
      )

      dispatch(setActiveOptionDisplayOrder(remaining[0]?.displayOrder ?? null))
    }
  }

  const OPTION_NAME_MAX_LENGTH = 50

  const handleRenameOption = (displayOrder, optionName) => {
    dispatch(
      updateOption({
        displayOrder,
        changes: {
          optionName: optionName.slice(0, OPTION_NAME_MAX_LENGTH),
        },
      }),
    )
  }

  return {
    proposalOptiontabs,
    proposalOptions,
    activeOption,

    setActiveOption: displayOrder =>
      dispatch(setActiveOptionDisplayOrder(displayOrder)),

    handleAddOption,
    handleDuplicateOption,
    handleDeleteOption,
    handleRenameOption,

    canAddMore: proposalOptions.length < MAX_PROPOSALS,
    canDelete: proposalOptions.length > MIN_PROPOSALS,
  }
}
